import { useEffect, useState } from "react";
import api, { fileUrl, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Trash2, Pencil, FileText, Upload, User, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function SalesmanForm({ initial, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState(
    initial || {
      name: "", mobile: "", email: "", address: "",
      aadhaar: "", pan: "", licence: "", voter_id: "",
      photo_path: "", joining_date: "",
      reference_person: "", reference_mobile: "",
      active: true,
    }
  );
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, photo_path: data.path });
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      data-testid="salesman-form"
    >
      <div className="flex items-start gap-4">
        <div className="w-24 h-28 rounded-md bg-zinc-100 overflow-hidden border border-border grid place-items-center">
          {form.photo_path ? (
            <img src={fileUrl(form.photo_path)} alt="" className="w-full h-full object-cover" />
          ) : (
            <User className="w-10 h-10 text-zinc-300" />
          )}
        </div>
        <div className="space-y-2">
          <Label>KYC Photo</Label>
          <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-border rounded-md cursor-pointer text-sm hover:bg-zinc-50">
            <Upload className="w-3.5 h-3.5" />
            {uploading ? "Uploading…" : (form.photo_path ? "Change" : "Upload")}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="salesman-photo-input" />
          </label>
          {form.photo_path && (
            <button type="button" className="block text-xs text-red-600 hover:underline" onClick={() => setForm({ ...form, photo_path: "" })}>
              Remove
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Full Name *</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="salesman-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Mobile *</Label>
          <Input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="salesman-mobile-input" />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Joining Date</Label>
          <Input type="date" value={form.joining_date || ""} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Aadhaar</Label>
          <Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>PAN</Label>
          <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} />
        </div>
        <div className="space-y-2">
          <Label>Driving Licence</Label>
          <Input value={form.licence} onChange={(e) => setForm({ ...form, licence: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Voter ID</Label>
          <Input value={form.voter_id} onChange={(e) => setForm({ ...form, voter_id: e.target.value })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Address</Label>
        <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Reference Person</Label>
          <Input value={form.reference_person} onChange={(e) => setForm({ ...form, reference_person: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Reference Mobile</Label>
          <Input value={form.reference_mobile} onChange={(e) => setForm({ ...form, reference_mobile: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="salesman-form-submit">
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function VoucherSalesmen() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = async (search = "") => {
    const { data } = await api.get("/voucher/salesmen", { params: search ? { q: search } : {} });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (form) => {
    try {
      const { data } = await api.post("/voucher/salesmen", form);
      toast.success("Salesman added");
      setOpen(false);
      setItems([data, ...items]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
  };
  const onUpdate = async (form) => {
    try {
      const { data } = await api.put(`/voucher/salesmen/${editTarget.id}`, form);
      toast.success("Salesman updated");
      setEditTarget(null);
      setItems(items.map((c) => (c.id === data.id ? data : c)));
    } catch {
      toast.error("Could not update");
    }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this salesman?")) return;
    try {
      await api.delete(`/voucher/salesmen/${id}`);
      setItems(items.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const openKycPdf = async (id) => {
    try {
      const r = await api.get(`/voucher/salesmen/${id}/kyc-pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Use direct download on mobile to avoid popup blockers
      const a = document.createElement("a");
      a.href = url;
      a.download = `kyc-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 200);
    } catch { toast.error("Could not open KYC PDF"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Voucher ERP</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Salesman Master</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="add-salesman-button">
              <Plus className="w-4 h-4 mr-1.5" /> Add Salesman
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Salesman (KYC)</DialogTitle>
            </DialogHeader>
            <SalesmanForm onSubmit={onCreate} submitLabel="Add Salesman" />
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
        <Input
          placeholder="Search by name, mobile, Aadhaar, PAN…"
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          className="pl-9"
          data-testid="salesman-search-input"
        />
      </div>

      <div className="md:hidden space-y-3" data-testid="salesman-mobile-list">
        {items.map((s) => (
          <Card key={s.id} className="p-4" data-testid={`salesman-mobile-${s.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-16 h-20 rounded-md bg-zinc-100 overflow-hidden border border-border shrink-0 grid place-items-center">
                {s.photo_path ? (
                  <img src={fileUrl(s.photo_path)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-6 h-6 text-zinc-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{s.name}</div>
                <div className="text-xs text-zinc-500 truncate">{s.mobile}</div>
                <div className="text-xs text-zinc-400 truncate">PAN: {s.pan || "—"} · Aadhaar: {s.aadhaar || "—"}</div>
                <div className="flex items-center gap-1 mt-2 flex-wrap">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openKycPdf(s.id)} data-testid={`kyc-pdf-${s.id}`}>
                    <Download className="w-3 h-3 mr-1" /> KYC PDF
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditTarget(s)} data-testid={`edit-salesman-${s.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  {isAdmin && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => onDelete(s.id)} data-testid={`delete-salesman-${s.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="salesman-desktop-list">
        {items.map((s) => (
          <Card key={s.id} className="p-4 flex gap-3">
            <div className="w-20 h-24 rounded-md bg-zinc-100 overflow-hidden border border-border shrink-0 grid place-items-center">
              {s.photo_path ? (
                <img src={fileUrl(s.photo_path)} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-zinc-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{s.name}</div>
              <div className="text-xs text-zinc-500 truncate">{s.mobile}</div>
              <div className="text-xs text-zinc-400 truncate">PAN: {s.pan || "—"} · Aadhaar: {s.aadhaar || "—"}</div>
              <div className="flex items-center gap-1 mt-2 flex-wrap">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openKycPdf(s.id)} data-testid={`kyc-pdf-${s.id}`}>
                  <Download className="w-3 h-3 mr-1" /> KYC PDF
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditTarget(s)} data-testid={`edit-salesman-${s.id}`}>
                  <Pencil className="w-3 h-3" />
                </Button>
                {isAdmin && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => onDelete(s.id)} data-testid={`delete-salesman-${s.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Salesman</DialogTitle>
          </DialogHeader>
          {editTarget && <SalesmanForm initial={editTarget} onSubmit={onUpdate} submitLabel="Save Changes" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
